<template>
  <div class="public-shop member-portal">
    <header class="shop-header member-dash-hero" :style="{ background: themeGradient }">
      <div class="container member-dash-hero-inner">
        <router-link :to="shopPath('')" class="member-auth-back">
          <i class="fas fa-arrow-left"></i> Kembali ke Toko
        </router-link>
        <div class="member-dash-top">
          <div class="member-dash-welcome">
            <div class="member-avatar">{{ memberInitials }}</div>
            <div>
              <h1 class="member-dash-greeting text-white">
                Halo, {{ memberFirstName }}!
              </h1>
              <p class="member-dash-email text-white" v-if="member">{{ member.email }}</p>
            </div>
          </div>
          <button class="member-btn-logout" @click="logout">
            <i class="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </div>
    </header>

    <div class="container pb-5" style="margin-top: -0.5rem;">
      <div v-if="loading" class="member-loading">
        <div class="member-loading-spinner"></div>
        <p class="text-muted">Memuat data member...</p>
      </div>

      <template v-else>
        <!-- Stats -->
        <div class="member-stats-row">
          <div class="member-stat-card">
            <div class="member-stat-icon"><i class="fas fa-shopping-bag"></i></div>
            <div class="member-stat-num">{{ orders.length }}</div>
            <div class="member-stat-label">Pesanan</div>
          </div>
          <div class="member-stat-card">
            <div class="member-stat-icon"><i class="fas fa-graduation-cap"></i></div>
            <div class="member-stat-num">{{ courses.length }}</div>
            <div class="member-stat-label">Kursus</div>
          </div>
          <div class="member-stat-card">
            <div class="member-stat-icon"><i class="fas fa-download"></i></div>
            <div class="member-stat-num">{{ digitalCount }}</div>
            <div class="member-stat-label">Digital</div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="member-tabs">
          <button
            v-for="t in tabs"
            :key="t.id"
            class="member-tab"
            :class="{ active: tab === t.id }"
            @click="tab = t.id"
          >
            <i :class="t.icon"></i>
            <span class="tab-label">{{ t.label }}</span>
          </button>
        </div>

        <!-- Orders -->
        <div v-show="tab === 'orders'" class="member-tab-panel">
          <div v-if="orders.length === 0" class="member-empty">
            <div class="member-empty-icon-wrap">
              <i class="fas fa-shopping-bag"></i>
            </div>
            <p class="member-empty-title">Belum ada pesanan</p>
            <p class="member-empty-desc">Pesanan Anda akan muncul di sini setelah checkout</p>
            <router-link :to="shopPath('')" class="member-btn-sm member-btn-solid mt-3">
              <i class="fas fa-store"></i> Belanja Sekarang
            </router-link>
          </div>
          <div v-for="o in orders" :key="o.order_id" class="member-item-card">
            <div class="member-order-header">
              <div>
                <code class="member-invoice">{{ o.invoice_number || o.order_id }}</code>
                <span class="member-badge ml-2" :class="paymentBadgeClass(o.payment_status)">
                  {{ paymentLabel(o.payment_status) }}
                </span>
              </div>
              <span class="member-order-amount">{{ formatRupiah(o.total_amount) }}</span>
            </div>
            <div class="member-order-date">
              <i class="far fa-calendar-alt mr-1"></i>{{ formatDate(o.created_at) }}
            </div>
            <ul class="member-order-items">
              <li v-for="it in o.items" :key="it.id">
                {{ it.product_name }} &times; {{ it.quantity }}
              </li>
            </ul>
            <router-link :to="shopPath('/order/' + o.order_id)" class="member-btn-sm member-btn-outline">
              <i class="fas fa-external-link-alt"></i> Lihat Detail
            </router-link>
          </div>
        </div>

        <!-- Courses -->
        <div v-show="tab === 'courses'" class="member-tab-panel">
          <div v-if="courses.length === 0" class="member-empty">
            <div class="member-empty-icon-wrap">
              <i class="fas fa-graduation-cap"></i>
            </div>
            <p class="member-empty-title">Belum ada kursus</p>
            <p class="member-empty-desc">Daftar kursus yang Anda beli akan tampil di sini</p>
            <router-link :to="shopPath('/kursus')" class="member-btn-sm member-btn-solid mt-3">
              <i class="fas fa-book-open"></i> Lihat Katalog Kursus
            </router-link>
          </div>
          <div v-for="c in courses" :key="c.enrollment_number" class="member-item-card">
            <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div class="flex-grow-1">
                <h3 class="member-course-title">{{ c.course_title }}</h3>
                <div class="member-progress-wrap">
                  <div class="member-progress-bar">
                    <div class="member-progress-fill" :style="{ width: c.progress_pct + '%' }"></div>
                  </div>
                </div>
                <span class="member-progress-text">Progress {{ c.progress_pct }}%</span>
              </div>
              <router-link
                v-if="c.course_slug"
                :to="shopPath('/kursus/' + c.course_slug + '/belajar?token=' + c.access_token)"
                class="member-btn-sm member-btn-solid"
              >
                <i class="fas fa-play"></i> Belajar
              </router-link>
            </div>
          </div>
        </div>

        <!-- Digital -->
        <div v-show="tab === 'digital'" class="member-tab-panel">
          <div v-if="digitalItems.length === 0" class="member-empty">
            <div class="member-empty-icon-wrap">
              <i class="fas fa-download"></i>
            </div>
            <p class="member-empty-title">Belum ada produk digital</p>
            <p class="member-empty-desc">Akses produk digital Anda akan muncul setelah pembelian</p>
          </div>
          <div v-for="(item, idx) in digitalItems" :key="idx" class="member-item-card">
            <h3 class="member-digital-title">
              <i class="fas fa-file-alt"></i>{{ item.product_name }}
            </h3>
            <small class="text-muted">Order: {{ item.order_id }}</small>
            <div class="member-digital-content" v-html="item.access_text"></div>
            <router-link :to="shopPath('/order/' + item.order_id)" class="member-btn-sm member-btn-outline mt-2">
              <i class="fas fa-receipt"></i> Halaman Order
            </router-link>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import { applyShopTheme, shopHeaderBackground, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import '@/components/shop_public/shop_public.css';
import '@/components/shop_public/member_portal.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  data() {
    return {
      shop: null,
      member: null,
      orders: [],
      courses: [],
      loading: true,
      tab: 'orders',
      tabs: [
        { id: 'orders', label: 'Pesanan', icon: 'fas fa-shopping-bag' },
        { id: 'courses', label: 'Kursus', icon: 'fas fa-graduation-cap' },
        { id: 'digital', label: 'Digital', icon: 'fas fa-download' },
      ],
    };
  },
  computed: {
    themeGradient() {
      return shopHeaderBackground(this.shop);
    },
    memberToken() {
      return this.$store.getters['public_shop/memberSession'](this.effectiveSlug)?.token || '';
    },
    digitalItems() {
      const items = [];
      for (const o of this.orders) {
        if (o.digital_access_items) {
          for (const d of o.digital_access_items) {
            items.push({ ...d, order_id: o.order_id });
          }
        }
      }
      return items;
    },
    digitalCount() {
      return this.digitalItems.length;
    },
    memberFirstName() {
      const name = this.member?.name || this.member?.email || 'Member';
      return name.split(' ')[0];
    },
    memberInitials() {
      const name = this.member?.name || this.member?.email || 'M';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },
  },
  async mounted() {
    if (!this.memberToken) {
      this.$router.replace(this.shopPath('/member/login'));
      return;
    }
    await this.load();
  },
  methods: {
    paymentBadgeClass(status) {
      if (status === 'paid') return 'member-badge-paid';
      if (status === 'pending') return 'member-badge-pending';
      return 'member-badge-other';
    },
    paymentLabel(status) {
      const map = { paid: 'Lunas', pending: 'Menunggu', expired: 'Kadaluarsa', cancelled: 'Batal' };
      return map[status] || status;
    },
    formatDate(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    async load() {
      this.loading = true;
      try {
        const r = await this.$store.dispatch('public_shop/member_dashboard', {
          slug: this.effectiveSlug,
          token: this.memberToken,
        });
        const data = r.data.data;
        this.shop = data.shop;
        this.member = data.member;
        this.orders = data.orders || [];
        this.courses = data.courses || [];
        applyShopTheme(this.shop);
      } catch (e) {
        this.$store.commit('public_shop/CLEAR_MEMBER_SESSION', this.effectiveSlug);
        this.$router.replace(this.shopPath('/member/login'));
      } finally {
        this.loading = false;
      }
    },
    async logout() {
      try {
        await this.$store.dispatch('public_shop/member_logout', {
          slug: this.effectiveSlug,
          token: this.memberToken,
        });
      } catch (_) { /* ignore */ }
      this.$store.commit('public_shop/CLEAR_MEMBER_SESSION', this.effectiveSlug);
      this.$router.push(this.shopPath('/member/login'));
    },
  },
};
</script>
